import type { ServerFullDto } from '@lagerfeuer/shared';
import { ApiError, errorMessage } from '../../lib/api';
import { appState } from '../../store/app';

/**
 * Einladungscode aus Nutzereingabe: voller Link (`https://host/invite/abc`), Pfad (`/invite/abc`),
 * `host/invite/abc` oder nur der Code bzw. die Vanity-URL. Liefert null bei leerer/ungültiger Eingabe.
 */
export function parseInviteInput(input: string): string | null {
  const raw = input.trim();
  if (!raw) return null;
  const m = /(?:^|\/)invite\/([^/?#\s]+)/i.exec(raw);
  const code = m ? decodeURIComponent(m[1]!) : raw;
  if (!/^[A-Za-z0-9_-]{2,64}$/.test(code)) return null;
  return code;
}

/** Ablaufzeit lesbar: „läuft in 3 Tagen ab" / „läuft nie ab". */
export function describeExpiry(expiresAt: string | null, now = Date.now()): string {
  if (!expiresAt) return 'läuft nie ab';
  const ms = new Date(expiresAt).getTime() - now;
  if (ms <= 0) return 'abgelaufen';
  const min = Math.round(ms / 60_000);
  if (min < 60) return `läuft in ${min} ${min === 1 ? 'Minute' : 'Minuten'} ab`;
  const h = Math.round(min / 60);
  if (h < 48) return `läuft in ${h} ${h === 1 ? 'Stunde' : 'Stunden'} ab`;
  const d = Math.round(h / 24);
  return `läuft in ${d} Tagen ab`;
}

/** Nach dem Beitritt/Anlegen: Server im Store ergänzen (das Gateway-Ereignis kann später kommen). */
export function addJoinedServer(full: ServerFullDto): void {
  const st = appState();
  if (!st.ready) return;
  if (!st.servers[full.id]) st.dispatch('SERVER_CREATE', full);
}

export const INVITE_AGES: { value: number; label: string }[] = [
  { value: 1800, label: '30 Minuten' },
  { value: 3600, label: '1 Stunde' },
  { value: 21600, label: '6 Stunden' },
  { value: 43200, label: '12 Stunden' },
  { value: 86400, label: '1 Tag' },
  { value: 604800, label: '7 Tage' },
  { value: 0, label: 'Nie' },
];

export const INVITE_USES: { value: number; label: string }[] = [
  { value: 0, label: 'Unbegrenzt' },
  { value: 1, label: '1 Nutzung' },
  { value: 5, label: '5 Nutzungen' },
  { value: 10, label: '10 Nutzungen' },
  { value: 25, label: '25 Nutzungen' },
  { value: 50, label: '50 Nutzungen' },
  { value: 100, label: '100 Nutzungen' },
];

/** Fehlertext für Einladungs-Fehler (ungültig, gebannt, Limit). */
export function inviteErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'INVITE_INVALID' || e.code === 'NOT_FOUND') return 'Diese Einladung ist ungültig oder abgelaufen.';
    if (e.code === 'BANNED') return 'Du wurdest von diesem Server gebannt.';
    if (e.code === 'LIMIT_REACHED') return 'Du bist bereits in der maximalen Anzahl an Servern.';
  }
  return errorMessage(e);
}
